import _ from 'lodash'
import { isObject, isInteger } from './isType'


/**
 * Filter object by key
 *
 * const obj = { foo: 1, bar: 2, zoo: 3 }
 *
 * object.filterByKey(obj, (key) => [ 'foo', 'bar' ].includes(key)) // { foo: 1, bar: 2 }
 *
 * @param {Object} obj
 * @param {Function} filter
 * @returns {*}
 */
const filterByKey = (obj, filter) =>
  Object.keys(obj).reduce((result, key) => {
    if (filter(key)) {
      result[key] = obj[key]
    }

    return result
  }, {})

/**
 * Filter object by value
 *
 * const obj = { foo: 1, bar: 2, zoo: 3 }
 *
 * object.filterByValue(obj, (value) => value > 1) // { bar: 2, zoo: 3 }
 *
 * @param {Object} obj
 * @param {Function} filter
 * @returns {*}
 */
const filterByValue = (obj, filter) =>
  Object.keys(obj).reduce((result, key) => {
    const value = obj[key]

    if (filter(value)) {
      result[key] = value
    }

    return result
  }, {})

/**
 * Get filtered object by keys
 * @param object {Object}
 * @param keys {Array}
 * @returns {Object}
 */
const filterKeysByArray = (object, keys) => keys.reduce((a, key) => {
  if (object[key]) {
    a[key] = object[key]
  }

  return a
}, {})

/**
 *
 * @param {Object} defaults
 * @param obj
 * @returns {*}
 */
const defaultsDeep = (defaults, obj) => {
  let copy

  if (defaults instanceof Array) {
    if (typeof obj !== 'undefined') {
      return obj
    }

    return defaults
  }

  // Handle Object
  if (defaults instanceof Object && obj instanceof Object) {
    copy = {}

    if (!Object.keys(defaults).length && Object.keys(obj).length) {
      copy = { ...obj }
    }
    else {
      for (let key in defaults) {
        if (!defaults.hasOwnProperty(key)) continue

        if (typeof obj[key] !== 'undefined') {
          copy[key] = defaultsDeep(defaults[key], obj[key])
        }
        else {
          copy[key] = defaults[key]
        }
      }
    }

    return copy
  }

  return obj
}

/**
 * Remove props by passed keys from object
 *
 * @param {Object} props
 * @param {...string} ignored
 * @returns {Object}
 */
const ignoreProps = (props = {}, ...ignored) => {
  const list = {}

  Object.keys(props).forEach((key) => {
    if (!ignored.includes(key)) {
      list[key] = props[key]
    }
  })

  return list
}

const diff = (object, base) => {
  const changes = (object, base) => (
    _.transform(object, (result, value, key) => {
      if (!_.isEqual(value, base[key])) {
        result[key] = (isObject(value) && isObject(base[key])) ? changes(value, base[key]) : value
      }
    })
  )

  return changes(object, base)
}

const mapKeys = (object, callback) => {
  if (typeof object === 'object') {
    const result = {}

    Object.keys(object).forEach((key) => {
      const newKey = callback(key)
      result[newKey] = object[key]
    })

    return result
  }

  console.warn(`helpers mapValues - ${object} is not object`)
  return object
}

const mapValues = (object, callback) => {
  if (typeof object === 'object') {
    const result = {}

    Object.keys(object).forEach((key) => {
      result[key] = callback(object[key], key)
    })

    return result
  }

  console.warn(`helpers mapValues - ${object} is not object`)
  return object
}

/**
 * Checks if object has property
 *
 * @param {string} prop property name
 * @param {Object} object object
 * @returns {boolean}
 */
const has = (prop, object) => Object.prototype.hasOwnProperty.call(object, prop)

/**
 * Converts string path to array path with integer indexes
 *
 * @param str
 * @returns {*}
 */
const stringToPath = (str) => (
  str.split('.').map((idx) => {
    const n = parseInt(idx, 10)

    if (!isNaN(n)) { // eslint-disable-line
      return n
    }

    return idx
  })
)

/**
 * Shallow merges of two objects. Always returns new object
 *
 * newObj = merge({ name: 'Fred', age: 10, marks: { math: 'A' } }, { age: 40, marks: { english: 'A' }, tag: 'a' })
 * // { name: 'Fred', age: 40, marks: { english: 'A' }, tag: 'a' }
 *
 * @param {Object} l
 * @param {Object} r
 * @return {Object}
 */
const merge = (l, r) => ({ ...l, ...r })

/**
 * Deep merge of two objects. Always returns new object. If a key exists in both objects:
 * - and both values are objects, the two values will be recursively merged
 * - otherwise the value from the second object will be used
 *
 * newObj = mergeDeep({ name: 'Fred', age: 10, marks: { math: 'A' }, arr: [1, 2, 3] }, { age: 40, marks: { english: 'A' }, arr: [10] })
 * // { name: 'Fred', age: 40, marks: { math: 'A', english: 'A' }, arr: [10] }
 *
 * @param {Object} l object
 * @param {Object} r override
 * @return {Object}
 */
const mergeDeep = (l, r) => {
  const result = {}

  for (let k in l) {
    if (has(k, l)) {
      if (has(k, r)) {
        result[k] = isObject(l[k]) && isObject(r[k]) ? mergeDeep(l[k], r[k]) : r[k]
      }
      else {
        result[k] = l[k]
      }
    }
  }

  for (let k in r) {
    if (has(k, r) && !(has(k, result))) {
      result[k] = r[k]
    }
  }

  return result
}

/**
 * Makes a shallow clone of an object, setting or overriding the specified property with the given value
 *
 * @param {Object} object
 * @param {string} key
 * @param {*} value
 * @returns {Object}
 */
const set = (object, key, value) => ({ ...object, [key]: value })

/**
 * Makes a shallow clone of an object, setting or overriding the nodes required to create the given path,
 * and placing the specific value at the tail end of that path
 *
 * @param {Object} object
 * @param {string|string[]} path
 * @param {*} value
 * @returns {Object|Array}
 */
const setIn = (object, path, value) => {
  if (typeof path === 'string') {
    path = stringToPath(path)
  }

  if (path.length === 0) {
    return value
  }

  const idx = path[0]
  let nextVal = value

  if (path.length > 1) {
    let nextObj

    if (object !== null && has(idx, object)) {
      nextObj = object[idx]
    }
    else if (isInteger(idx)) {
      nextObj = []
    }
    else {
      nextObj = {}
    }

    nextVal = setIn(nextObj, path.slice(1), value)
  }

  if (isInteger(idx) && Array.isArray(object)) {
    const arr = [ ...object ]

    arr[idx] = nextVal

    return arr
  }

  return { ...object, [idx]: nextVal }
}

/**
 * Makes a shallow clone of an object, setting or overriding the specified property by calling function
 *
 * @param {Object} object
 * @param {string} key
 * @param {function} fn
 * @returns {Object|Array}
 */
const update = (object, key, fn) => updateIn(object, [key], fn)

/**
 * Makes a shallow clone of an object, setting or overriding the nodes required to create the given path,
 * and placing the specific value returned by function at the tail end of that path
 *
 * @param {Object} value
 * @param {string|Array} path
 * @param {function} fn
 * @returns {Object|Array}
 */
const updateIn = (value, path, fn) => {
  if (typeof path === 'string') {
    path = stringToPath(path)
  }

  if (path.length === 0) {
    return fn(value)
  }

  const idx = path[0]
  let nextVal

  if (path.length > 1) {
    let nextObj

    if (value !== null && has(idx, value)) {
      nextObj = value[idx]
    }
    else if (isInteger(idx)) {
      nextObj = []
    }
    else {
      nextObj = {}
    }

    nextVal = updateIn(nextObj, path.slice(1), fn)
  }
  else {
    nextVal = fn(value[idx])
  }

  if (isInteger(idx) && Array.isArray(value)) {
    const arr = [ ...value ]

    arr[idx] = nextVal

    return arr
  }

  return { ...value, [idx]: nextVal }
}

/**
 * Remove key / index from object / array. Returns a new object / array
 *
 * @param {Array|Object} value
 * @param {string|integer} key
 * @returns {Object|Array}
 */
const remove = (value, key) => {
  if (isInteger(key) && Array.isArray(value)) {
    const result = [ ...value ]
    result.splice(key, 1)
    return result
  }

  const res = { ...value }
  delete res[key]
  return res
}

/**
 * Remove value in object by path
 *
 * @param {Object|Array} value
 * @param {string|Array} path
 * @returns {Object|Array}
 */
const removeIn = (value, path) => {
  if (typeof path === 'string') {
    path = stringToPath(path)
  }

  if (path.length === 0) {
    return value
  }

  if (path.length === 1) {
    return remove(value, path[0])
  }

  const head = path[0]
  const tail = path.slice(1)

  if (value[head] == null) {
    return value
  }

  return set(value, head, removeIn(value[head], tail))
}

/**
 * Get object param
 * example const fooObject = { foo: { bar: { value: 1 } } } →  const value = get(fooObject, 'foo.bar.value') === 1
 */
const get = (object, path, defaultValue) => {
  if (
    object === null
    || object === undefined
    || Array.isArray(object)
    || typeof object !== 'object'
  ) {
    console.warn('Type off object param is not Object type ', object)

    if (defaultValue) {
      return defaultValue
    }

    return null
  }

  if (typeof path !== 'string') {
    console.warn('Type off path param is not String type ', object)
    return null
  }

  const pathArrValue = path.split('.')
  let result = typeof window !== 'undefined' && object === window ? window : { ...object }
  let index = 0

  while (result !== null && index < pathArrValue.length) {
    const value = result[pathArrValue[index]]

    if (!value) {
      result = null
    }
    else {
      result = value
      index += 1
    }
  }

  if (!result) {
    return defaultValue
  }

  return result
}


export default {
  filterByKey,
  filterByValue,
  filterKeysByArray,
  diff,
  mapKeys,
  mapValues,
  ignoreProps,
  defaultsDeep,
  merge,
  mergeDeep,
  has,
  isObject,
  set,
  setIn,
  update,
  updateIn,
  remove,
  removeIn,
  get,
}
